// Concierge.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Animated,
  Easing,
  Image,
  Linking,
  Platform,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";

const API_BASE = "https://collabbay.com/APIControllers";
const FETCH_CONCIERGE_OFFERS_URL = `${API_BASE}/GetCreatorConciergeOffers.php`;

const FALLBACK_IMAGE =
  "https://images.pexels.com/photos/1058277/pexels-photo-1058277.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1";

const MAX_RESULTS = 15;
const MINIMUM_LOADING_MS = 2000;
const BLUE = "#2f7dff";

const categoryOptions = [
  { label: "Restaurant", value: "Restaurant", icon: "restaurant-outline" },
  { label: "Bar", value: "Bar", icon: "wine-outline" },
  { label: "Activity", value: "Activity", icon: "pulse-outline" },
];

const creditOptions = [
  {
    label: "£0 to £50",
    min: "0",
    max: "50",
    note: "Highly likely to accept",
  },
  {
    label: "£50 to £100",
    min: "50",
    max: "100",
    note: "Recommended · likely to accept",
  },
  {
    label: "£100 plus",
    min: "100",
    max: "",
    note: "",
  },
];

const dayOptions = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const timeOptions = [
  "12:00 PM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
  "5:00 PM",
  "6:00 PM",
  "7:00 PM",
  "8:00 PM",
  "9:00 PM",
  "10:00 PM",
];

const loadingLines = [
  "Handpicking your offers",
  "Finding the very best fits",
  "Checking venue availability",
  "Preparing your batch shortlist",
];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getNextDateForDay = (dayName) => {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const now = new Date();
  const targetIndex = days.indexOf(dayName);
  const currentIndex = now.getDay();

  let diff = targetIndex - currentIndex;
  if (diff <= 0) diff += 7;

  const result = new Date(now);
  result.setDate(now.getDate() + diff);

  return result;
};

const formatDateForBackend = (dayName, timeString) => {
  const date = getNextDateForDay(dayName);

  const formattedDate = date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return `${formattedDate} ${timeString}`;
};

const firstImageFromCsv = (value) => {
  if (!value || !String(value).trim()) return FALLBACK_IMAGE;

  const first = String(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)[0];

  if (!first) return FALLBACK_IMAGE;

  if (first.startsWith("http://") || first.startsWith("https://")) {
    return first.replace(/ /g, "%20");
  }

  if (first.startsWith("/")) {
    return `https://collabbay.com${first}`.replace(/ /g, "%20");
  }

  return `https://collabbay.com/${first}`.replace(/ /g, "%20");
};

const getDisplayName = (item) => {
  if (item?.OnBehalfOf && String(item.OnBehalfOf).trim() !== "") {
    return String(item.OnBehalfOf).trim();
  }

  return item?.BrandName || "Venue";
};

const getDisplayNeighbourhood = (item) => {
  if (item?.Neighbourhood && String(item.Neighbourhood).trim() !== "") {
    return String(item.Neighbourhood).trim();
  }

  if (item?.Location && String(item.Location).trim() !== "") {
    return String(item.Location).trim();
  }

  return "";
};

const normaliseBrandUrl = (url) => {
  if (!url || String(url).trim() === "") return "";

  const clean = String(url).trim();

  if (clean.startsWith("http://") || clean.startsWith("https://")) {
    return clean;
  }

  return `https://${clean}`;
};

export default function Concierge({ navigation, route }) {
  const [step, setStep] = useState(1);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedTimes, setSelectedTimes] = useState([]);

  const [offers, setOffers] = useState([]);
  const [selectedListingIds, setSelectedListingIds] = useState([]);
  const [brokenImageIds, setBrokenImageIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [conciergeLoading, setConciergeLoading] = useState(false);
  const [loadingLineIndex, setLoadingLineIndex] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;
  const cardPulseAnim = useRef(new Animated.Value(1)).current;
  const loadingFadeAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    const incomingCategory =
      route?.params?.OfferCategory ||
      route?.params?.selectedCategory ||
      route?.params?.category;

    if (incomingCategory && String(incomingCategory).trim() !== "") {
      setSelectedCategory(String(incomingCategory).trim());
    }
  }, [route?.params]);

  useEffect(() => {
    fadeAnim.setValue(0);
    slideAnim.setValue(16);

    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 280,
        easing: Easing.out(Easing.ease),
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 280,
        easing: Easing.out(Easing.ease),
        useNativeDriver: true,
      }),
    ]).start();
  }, [step, fadeAnim, slideAnim]);

  useEffect(() => {
    Animated.sequence([
      Animated.timing(cardPulseAnim, {
        toValue: 0.992,
        duration: 80,
        easing: Easing.out(Easing.ease),
        useNativeDriver: true,
      }),
      Animated.timing(cardPulseAnim, {
        toValue: 1,
        duration: 120,
        easing: Easing.out(Easing.ease),
        useNativeDriver: true,
      }),
    ]).start();
  }, [selectedListingIds.length, cardPulseAnim]);

  useEffect(() => {
    if (!conciergeLoading) return undefined;

    const interval = setInterval(() => {
      Animated.sequence([
        Animated.timing(loadingFadeAnim, {
          toValue: 0,
          duration: 160,
          useNativeDriver: true,
        }),
        Animated.timing(loadingFadeAnim, {
          toValue: 1,
          duration: 220,
          useNativeDriver: true,
        }),
      ]).start();

      setLoadingLineIndex((prev) => (prev + 1) % loadingLines.length);
    }, 650);

    return () => clearInterval(interval);
  }, [conciergeLoading, loadingFadeAnim]);

  const primaryDay = selectedDays[0] || "";
  const primaryTime = selectedTimes[0] || "";

  const proposedDate = useMemo(() => {
    if (!primaryDay || !primaryTime) return "";
    return formatDateForBackend(primaryDay, primaryTime);
  }, [primaryDay, primaryTime]);

  const selectedSlots = useMemo(() => {
    const slots = [];

    selectedDays.forEach((day) => {
      selectedTimes.forEach((time) => {
        slots.push({
          day,
          time,
          proposedDate: formatDateForBackend(day, time),
        });
      });
    });

    return slots;
  }, [selectedDays, selectedTimes]);

  const limitedOffers = useMemo(() => {
    return offers.slice(0, MAX_RESULTS);
  }, [offers]);

  const selectedOffers = useMemo(() => {
    return limitedOffers.filter((offer) =>
      selectedListingIds.includes(String(offer.ListingID))
    );
  }, [limitedOffers, selectedListingIds]);

  const canFetch =
    selectedCategory &&
    selectedCredit &&
    selectedDays.length > 0 &&
    selectedTimes.length > 0 &&
    !loading &&
    !conciergeLoading;

  const toggleArrayValue = (setter, value) => {
    setter((prev) => {
      if (prev.includes(value)) {
        return prev.filter((item) => item !== value);
      }

      return [...prev, value];
    });
  };

  const goNext = () => {
    if (step === 1 && !selectedCategory) {
      Alert.alert("Select category", "Please choose an offer category first.");
      return;
    }

    if (step === 2 && !selectedCredit) {
      Alert.alert("Select value", "Please choose a value range first.");
      return;
    }

    if (step === 3 && (selectedDays.length === 0 || selectedTimes.length === 0)) {
      Alert.alert(
        "Select availability",
        "Please choose at least one day and one time."
      );
      return;
    }

    setStep((prev) => Math.min(prev + 1, 4));
  };

  const goBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const fetchMatchedOffers = async () => {
    if (!canFetch) {
      Alert.alert("Missing details", "Please complete all filters first.");
      return;
    }

    setLoading(true);
    setConciergeLoading(true);
    setLoadingLineIndex(0);
    setErrorMessage("");
    setOffers([]);
    setSelectedListingIds([]);
    setBrokenImageIds([]);

    const startedAt = Date.now();

    try {
      const response = await fetch(FETCH_CONCIERGE_OFFERS_URL, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          OfferCategory: selectedCategory,
          CreditMin: selectedCredit.min,
          CreditMax: selectedCredit.max,
          PreferredDays: selectedDays,
          PreferredTimes: selectedTimes,
          PreferredDay: primaryDay,
          PreferredTime: primaryTime,
        }),
      });

      const rawText = await response.text();

      let data;
      try {
        data = JSON.parse(rawText);
      } catch (jsonError) {
        throw new Error(
          `PHP did not return valid JSON.\n\nHTTP Status: ${response.status}\n\nRaw response:\n${rawText.substring(
            0,
            900
          )}`
        );
      }

      if (!data.success) {
        throw new Error(data.message || "PHP returned an unknown error.");
      }

      const elapsed = Date.now() - startedAt;
      if (elapsed < MINIMUM_LOADING_MS) {
        await wait(MINIMUM_LOADING_MS - elapsed);
      }

      const fetchedOffers = Array.isArray(data.offers) ? data.offers : [];
      const cappedOffers = fetchedOffers.slice(0, MAX_RESULTS);

      setOffers(cappedOffers);
      setSelectedListingIds(cappedOffers.map((offer) => String(offer.ListingID)));
      setStep(4);
    } catch (error) {
      const elapsed = Date.now() - startedAt;
      if (elapsed < MINIMUM_LOADING_MS) {
        await wait(MINIMUM_LOADING_MS - elapsed);
      }

      setErrorMessage(error.message);
      Alert.alert("Concierge fetch failed", error.message);
    } finally {
      setLoading(false);
      setConciergeLoading(false);
    }
  };

  const toggleOffer = (listingId) => {
    const id = String(listingId);

    setSelectedListingIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((existingId) => existingId !== id);
      }

      return [...prev, id];
    });
  };

  const selectAll = () => {
    setSelectedListingIds(limitedOffers.map((offer) => String(offer.ListingID)));
  };

  const deselectAll = () => {
    setSelectedListingIds([]);
  };

  const openBrandURL = (url) => {
    const cleanUrl = normaliseBrandUrl(url);

    if (!cleanUrl) {
      Alert.alert("No link", "This venue does not have a website link attached.");
      return;
    }

    if (Platform.OS === "web") {
      window.open(cleanUrl, "_blank");
    } else {
      Linking.openURL(cleanUrl);
    }
  };

  const handlePrepareBulkBookings = () => {
    if (selectedOffers.length === 0) {
      Alert.alert("No offers selected", "Please select at least one offer.");
      return;
    }

    const payload = selectedOffers.map((offer) => ({
      ListingID: offer.ListingID,
      BrandName: offer.BrandName,
      DisplayName: getDisplayName(offer),
      CreditAvailableStart: proposedDate,
      SelectedDay: primaryDay,
      SelectedTime: primaryTime,
      SelectedSlots: selectedSlots,
    }));

    console.log("Prepared Concierge Bulk Booking Payload:", payload);

    Alert.alert(
      "Batch booking ready",
      `${selectedOffers.length} booking request(s) are ready to send.\n\nPrimary proposed date: ${proposedDate}\n\nIf multiple venues accept, you’ll need to amend/manage dates to avoid clashes.`
    );
  };

  const renderProgress = () => (
    <View style={styles.progressRow}>
      {[1, 2, 3, 4].map((item) => (
        <View
          key={item}
          style={[
            styles.progressChip,
            step === item && styles.progressChipActive,
            step > item && styles.progressChipComplete,
          ]}
        >
          <Text
            style={[
              styles.progressText,
              step === item && styles.progressTextActive,
              step > item && styles.progressTextComplete,
            ]}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );

  const renderHeaderSelectedPill = () => {
    if (step !== 4) return null;

    return (
      <View style={styles.topSelectedPill}>
        <Ionicons name="checkmark-circle" size={16} color="#fff" />
        <Text style={styles.topSelectedPillText}>
          {selectedOffers.length} selected
        </Text>
      </View>
    );
  };

  const renderCategoryStep = () => (
    <Animated.View
      style={[
        styles.card,
        { opacity: fadeAnim, transform: [{ translateY: slideAnim }] },
      ]}
    >
      <Text style={styles.eyebrow}>Concierge brief</Text>
      <Text style={styles.stepTitle}>What are you looking for?</Text>
      <Text style={styles.stepText}>
        Choose the type of venue. We’ll reverse-search the network and bring
        back suitable options.
      </Text>

      <View style={styles.optionGrid}>
        {categoryOptions.map((option) => {
          const active = selectedCategory === option.value;

          return (
            <TouchableOpacity
              key={option.value}
              style={[styles.optionCard, active && styles.optionCardActive]}
              onPress={() => setSelectedCategory(option.value)}
              activeOpacity={0.86}
            >
              <Ionicons
                name={option.icon}
                size={25}
                color={active ? "#fff" : "#111"}
              />
              <Text
                style={[styles.optionTitle, active && styles.optionTitleActive]}
              >
                {option.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      <TouchableOpacity style={styles.primaryButton} onPress={goNext}>
        <Text style={styles.primaryButtonText}>Continue</Text>
      </TouchableOpacity>
    </Animated.View>
  );

  const renderCreditStep = () => (
    <Animated.View
      style={[
        styles.card,
        { opacity: fadeAnim, transform: [{ translateY: slideAnim }] },
      ]}
    >
      <Text style={styles.eyebrow}>Offer value</Text>
      <Text style={styles.stepTitle}>Set your value range</Text>
      <Text style={styles.stepText}>
        Choose the type of gifted offer you’d like Concierge to find for you.
      </Text>

      <View style={styles.optionGrid}>
        {creditOptions.map((option) => {
          const active = selectedCredit?.label === option.label;

          return (
            <TouchableOpacity
              key={option.label}
              style={[styles.valueOptionCard, active && styles.valueOptionCardActive]}
              onPress={() => setSelectedCredit(option)}
              activeOpacity={0.86}
            >
              <Text
                style={[
                  styles.valueOptionTitle,
                  active && styles.valueOptionTitleActive,
                ]}
              >
                {option.label}
              </Text>

              {!!option.note && (
                <Text
                  style={[
                    styles.valueOptionNote,
                    active && styles.valueOptionNoteActive,
                  ]}
                >
                  {option.note}
                </Text>
              )}
            </TouchableOpacity>
          );
        })}
      </View>

      <View style={styles.buttonRow}>
        <TouchableOpacity style={styles.secondaryButton} onPress={goBack}>
          <Text style={styles.secondaryButtonText}>Back</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.primaryButtonFlex} onPress={goNext}>
          <Text style={styles.primaryButtonText}>Continue</Text>
        </TouchableOpacity>
      </View>
    </Animated.View>
  );

  const renderAvailabilityStep = () => (
    <Animated.View
      style={[
        styles.card,
        { opacity: fadeAnim, transform: [{ translateY: slideAnim }] },
      ]}
    >
      <Text style={styles.eyebrow}>Availability</Text>
      <Text style={styles.stepTitle}>When could you go?</Text>
      <Text style={styles.stepText}>
        Pick a few possible days and times. We’ll match any venue that can host
        one of those slots.
      </Text>

      <Text style={styles.label}>Days that work</Text>
      <View style={styles.wrapRow}>
        {dayOptions.map((day) => {
          const active = selectedDays.includes(day);

          return (
            <TouchableOpacity
              key={day}
              style={[styles.smallPill, active && styles.smallPillActive]}
              onPress={() => toggleArrayValue(setSelectedDays, day)}
            >
              <Text
                style={[
                  styles.smallPillText,
                  active && styles.smallPillTextActive,
                ]}
              >
                {day}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      <Text style={styles.label}>Times that work</Text>
      <View style={styles.wrapRow}>
        {timeOptions.map((time) => {
          const active = selectedTimes.includes(time);

          return (
            <TouchableOpacity
              key={time}
              style={[styles.smallPill, active && styles.smallPillActive]}
              onPress={() => toggleArrayValue(setSelectedTimes, time)}
            >
              <Text
                style={[
                  styles.smallPillText,
                  active && styles.smallPillTextActive,
                ]}
              >
                {time}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {selectedSlots.length > 0 ? (
        <View style={styles.slotPreviewBox}>
          <Text style={styles.slotPreviewTitle}>Selected slots</Text>
          <Text style={styles.slotPreviewText}>
            {selectedSlots
              .slice(0, 5)
              .map((slot) => `${slot.day} ${slot.time}`)
              .join(" · ")}
            {selectedSlots.length > 5 ? ` +${selectedSlots.length - 5} more` : ""}
          </Text>
        </View>
      ) : null}

      {proposedDate ? (
        <View style={styles.warningBox}>
          <Ionicons name="warning-outline" size={18} color="#8a5a00" />
          <Text style={styles.warningText}>
            Batch requests will use the first selected slot as the primary
            proposed date: {proposedDate}. If multiple venues accept, you’ll
            need to amend/manage dates.
          </Text>
        </View>
      ) : null}

      <View style={styles.buttonRow}>
        <TouchableOpacity style={styles.secondaryButton} onPress={goBack}>
          <Text style={styles.secondaryButtonText}>Back</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.primaryButtonFlex, !canFetch && styles.disabledButton]}
          onPress={fetchMatchedOffers}
          disabled={!canFetch}
        >
          {loading ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.primaryButtonText}>Find matched offers</Text>
          )}
        </TouchableOpacity>
      </View>
    </Animated.View>
  );

  const renderConciergeLoading = () => (
    <Animated.View
      style={[
        styles.loadingCard,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <View style={styles.loadingSparkle}>
        <Text style={styles.loadingSparkleText}>✨</Text>
      </View>

      <Text style={styles.loadingTitle}>collabbay™ Concierge</Text>

      <Animated.Text style={[styles.loadingLine, { opacity: loadingFadeAnim }]}>
        {loadingLines[loadingLineIndex]}
      </Animated.Text>

      <View style={styles.loadingDotsRow}>
        <View style={styles.loadingDot} />
        <View style={[styles.loadingDot, styles.loadingDotMid]} />
        <View style={styles.loadingDot} />
      </View>

      <Text style={styles.loadingSub}>
        Matching your availability, preferred value and venue style.
      </Text>
    </Animated.View>
  );

  const renderRecapSlider = () => (
    <View style={styles.recapShell}>
      <View style={styles.recapHeaderRow}>
        <View>
          <Text style={styles.recapTitle}>Your quick picks</Text>
          <Text style={styles.recapSub}>
            Swipe through and tap to select or remove.
          </Text>
        </View>

        <View style={styles.recapCountPill}>
          <Text style={styles.recapCountText}>{selectedOffers.length}</Text>
        </View>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.recapScroll}
      >
        {limitedOffers.map((offer) => {
          const selected = selectedListingIds.includes(String(offer.ListingID));
          const imageUri = brokenImageIds.includes(String(offer.ListingID))
            ? FALLBACK_IMAGE
            : firstImageFromCsv(
                offer.LogoImage ||
                  offer.MainImage ||
                  offer.Image ||
                  offer.ProfileImage ||
                  offer.ListingImage
              );

          return (
            <TouchableOpacity
              key={`recap-${offer.ListingID}`}
              style={[styles.recapCard, selected && styles.recapCardSelected]}
              onPress={() => toggleOffer(offer.ListingID)}
              activeOpacity={0.86}
            >
              <Image
                source={{ uri: imageUri }}
                style={styles.recapImage}
                onError={() =>
                  setBrokenImageIds((prev) => [
                    ...new Set([...prev, String(offer.ListingID)]),
                  ])
                }
              />

              <View style={styles.recapOverlay} />

              <View style={styles.recapCardTop}>
                <View
                  style={[
                    styles.recapSelectBubble,
                    selected && styles.recapSelectBubbleActive,
                  ]}
                >
                  <Ionicons
                    name={selected ? "checkmark" : "add"}
                    size={14}
                    color={selected ? "#fff" : "#111"}
                  />
                </View>
              </View>

              <View style={styles.recapCardBottom}>
                <Text style={styles.recapCardTitle} numberOfLines={2}>
                  {getDisplayName(offer)}
                </Text>

                {!!getDisplayNeighbourhood(offer) && (
                  <Text style={styles.recapArea} numberOfLines={1}>
                    {getDisplayNeighbourhood(offer)}
                  </Text>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );

  const renderResultsStep = () => (
    <Animated.View
      style={[
        styles.card,
        { opacity: fadeAnim, transform: [{ translateY: slideAnim }] },
      ]}
    >
      <View style={styles.resultsHeader}>
        <View style={{ flex: 1 }}>
          <Text style={styles.eyebrow}>Your matches</Text>
          <Text style={styles.stepTitle}>Matched offers</Text>
          <Text style={styles.stepText}>
            Showing up to {MAX_RESULTS}. {selectedOffers.length} selected.
          </Text>
        </View>

        <TouchableOpacity style={styles.iconButton} onPress={fetchMatchedOffers}>
          <Ionicons name="refresh-outline" size={20} color="#111" />
        </TouchableOpacity>
      </View>

      <View style={styles.selectedSummaryBar}>
        <Text style={styles.selectedSummaryText}>
          {selectedOffers.length} selected
        </Text>
        <Text style={styles.selectedSummarySub}>
          Tap any offer card to select or deselect it.
        </Text>
      </View>

      {renderRecapSlider()}

      <View style={styles.batchHero}>
        <Text style={styles.batchHeroTitle}>Ready to send your batch</Text>
        <Text style={styles.batchHeroText}>
          {selectedOffers.length} selected request
          {selectedOffers.length === 1 ? "" : "s"} will use {proposedDate}.
        </Text>

        <TouchableOpacity
          style={[
            styles.batchButton,
            selectedOffers.length === 0 && styles.disabledButton,
          ]}
          onPress={handlePrepareBulkBookings}
          disabled={selectedOffers.length === 0}
        >
          <Ionicons name="send-outline" size={18} color="#fff" />
          <Text style={styles.batchButtonText}>
            Send booking to all {selectedOffers.length} selected
          </Text>
        </TouchableOpacity>

        <Text style={styles.batchFootnote}>
          If multiple venues accept, you’ll need to amend/manage dates.
        </Text>
      </View>

      <Text style={styles.tapGuideText}>
        Tap to select or deselect individual offers.
      </Text>

      <View style={styles.buttonRow}>
        <TouchableOpacity style={styles.largeSelectButton} onPress={selectAll}>
          <Ionicons name="checkmark-circle-outline" size={18} color="#111" />
          <Text style={styles.largeSelectButtonText}>Select all</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.largeSelectButton} onPress={deselectAll}>
          <Ionicons name="close-circle-outline" size={18} color="#111" />
          <Text style={styles.largeSelectButtonText}>Deselect all</Text>
        </TouchableOpacity>
      </View>

      {limitedOffers.length === 0 ? (
        <View style={styles.emptyBox}>
          <Text style={styles.emptyTitle}>No matches found</Text>
          <Text style={styles.emptyText}>
            Try widening the value range or changing day/time.
          </Text>
        </View>
      ) : (
        limitedOffers.map((offer) => {
          const selected = selectedListingIds.includes(String(offer.ListingID));
          const displayName = getDisplayName(offer);
          const neighbourhood = getDisplayNeighbourhood(offer);
          const brandUrl = normaliseBrandUrl(offer.BrandURL);
          const imageUri = brokenImageIds.includes(String(offer.ListingID))
            ? FALLBACK_IMAGE
            : firstImageFromCsv(
                offer.LogoImage ||
                  offer.MainImage ||
                  offer.Image ||
                  offer.ProfileImage ||
                  offer.ListingImage
              );

          return (
            <Animated.View
              key={String(offer.ListingID)}
              style={{ transform: [{ scale: cardPulseAnim }] }}
            >
              <TouchableOpacity
                style={[
                  styles.offerCard,
                  selected
                    ? styles.offerCardSelected
                    : styles.offerCardDeselected,
                ]}
                onPress={() => toggleOffer(offer.ListingID)}
                activeOpacity={0.88}
              >
                <View style={styles.offerImageWrap}>
                  <Image
                    source={{ uri: imageUri }}
                    style={styles.offerImage}
                    onError={() =>
                      setBrokenImageIds((prev) => [
                        ...new Set([...prev, String(offer.ListingID)]),
                      ])
                    }
                  />

                  <TouchableOpacity
                    style={[
                      styles.statusRibbon,
                      selected
                        ? styles.statusRibbonSelected
                        : styles.statusRibbonDeselected,
                    ]}
                    onPress={() => toggleOffer(offer.ListingID)}
                    activeOpacity={0.9}
                  >
                    <Ionicons
                      name={
                        selected ? "checkmark-circle" : "add-circle-outline"
                      }
                      size={18}
                      color={selected ? "#fff" : "#111"}
                    />
                    <Text
                      style={[
                        styles.statusRibbonText,
                        !selected && styles.statusRibbonTextDeselected,
                      ]}
                    >
                      {selected ? "Selected" : "Tap to select"}
                    </Text>
                  </TouchableOpacity>
                </View>

                <View style={styles.offerBody}>
                  <View style={styles.offerTop}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.offerTitle}>{displayName}</Text>

                      {!!neighbourhood && (
                        <View style={styles.neighbourhoodHero}>
                          <Ionicons
                            name="location-outline"
                            size={17}
                            color="#111"
                          />
                          <Text style={styles.neighbourhoodHeroText}>
                            {neighbourhood}
                          </Text>
                        </View>
                      )}
                    </View>

                    <TouchableOpacity
                      style={[
                        styles.selectBadge,
                        selected && styles.selectBadgeActive,
                      ]}
                      onPress={() => toggleOffer(offer.ListingID)}
                    >
                      <Ionicons
                        name={selected ? "checkmark" : "add"}
                        size={18}
                        color={selected ? "#fff" : "#111"}
                      />
                    </TouchableOpacity>
                  </View>

                  <View style={styles.metaRow}>
                    <Text style={styles.valueText}>£{offer.Credits} value</Text>

                    {!!brandUrl && (
                      <TouchableOpacity
                        style={styles.websiteChip}
                        onPress={() => openBrandURL(brandUrl)}
                      >
                        <Ionicons name="open-outline" size={15} color="#111" />
                        <Text style={styles.websiteChipText}>See Brand</Text>
                      </TouchableOpacity>
                    )}
                  </View>

                  <View style={styles.offerProminentBox}>
                    <Text style={styles.offerProminentLabel}>Offer</Text>
                    <Text style={styles.offerProminentText}>
                      {offer.NonCreditOffer || "No offer details available"}
                    </Text>
                  </View>

                  <View style={styles.detailBoxBlack}>
                    <Text style={styles.detailLabelWhite}>
                      Primary proposed date
                    </Text>
                    <Text style={styles.detailTextWhite}>{proposedDate}</Text>
                  </View>
                </View>
              </TouchableOpacity>
            </Animated.View>
          );
        })
      )}
    </Animated.View>
  );

  return (
    <SafeAreaView style={styles.safe}>
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => {
              if (navigation && navigation.goBack) {
                navigation.goBack();
              }
            }}
          >
            <Ionicons name="chevron-back" size={22} color="#111" />
          </TouchableOpacity>

          <View style={{ flex: 1 }}>
            <Text style={styles.title}>
              collabbay™ <Text style={styles.conciergeWord}>✨ Concierge</Text>
            </Text>
            <Text style={styles.subtitle}>
              Your personal Collabbay concierge. Set your availability,
              interests and desired offers, get hand-picked suited offers, and
              send requests in bulk.
            </Text>
          </View>
        </View>

        {renderHeaderSelectedPill()}

        {renderProgress()}

        {errorMessage ? (
          <View style={styles.errorBox}>
            <Text style={styles.errorTitle}>Error</Text>
            <Text style={styles.errorText}>{errorMessage}</Text>
          </View>
        ) : null}

        {conciergeLoading && renderConciergeLoading()}

        {!conciergeLoading && step === 1 && renderCategoryStep()}
        {!conciergeLoading && step === 2 && renderCreditStep()}
        {!conciergeLoading && step === 3 && renderAvailabilityStep()}
        {!conciergeLoading && step === 4 && renderResultsStep()}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: "#f4f4f4",
  },
  container: {
    width: "100%",
    maxWidth: 760,
    alignSelf: "center",
    padding: 18,
    paddingBottom: 80,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 14,
    marginBottom: 14,
    marginTop: Platform.OS === "web" ? 18 : 4,
  },
  backButton: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#e8e8e8",
    marginTop: 2,
  },
  title: {
    fontSize: 27,
    fontWeight: "700",
    color: "#111",
    letterSpacing: -0.4,
    lineHeight: 34,
  },
  conciergeWord: {
    color: "#111",
    backgroundColor: "#fdf05d",
    paddingHorizontal: 6,
    borderRadius: 10,
    overflow: "hidden",
    fontWeight: "800",
  },
  subtitle: {
    fontSize: 13,
    color: "#666",
    marginTop: 8,
    lineHeight: 19,
    fontWeight: "600",
  },
  topSelectedPill: {
    alignSelf: "flex-start",
    backgroundColor: "#111",
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 14,
  },
  topSelectedPillText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "900",
  },
  progressRow: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 16,
  },
  progressChip: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "#e9e9e9",
    alignItems: "center",
    justifyContent: "center",
  },
  progressChipActive: {
    backgroundColor: "#111",
  },
  progressChipComplete: {
    backgroundColor: "#dff4e6",
  },
  progressText: {
    color: "#777",
    fontWeight: "900",
  },
  progressTextActive: {
    color: "#fff",
  },
  progressTextComplete: {
    color: "#087a31",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 26,
    padding: 18,
    borderWidth: 1,
    borderColor: "#e8e8e8",
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 7 },
  },
  eyebrow: {
    fontSize: 12,
    fontWeight: "900",
    color: "#777",
    textTransform: "uppercase",
    letterSpacing: 1,
    marginBottom: 8,
  },
  stepTitle: {
    fontSize: 24,
    fontWeight: "900",
    color: "#111",
    marginBottom: 8,
  },
  stepText: {
    fontSize: 14,
    color: "#666",
    lineHeight: 21,
    marginBottom: 18,
  },
  optionGrid: {
    gap: 12,
  },
  optionCard: {
    borderWidth: 1,
    borderColor: "#e3e3e3",
    borderRadius: 20,
    padding: 18,
    backgroundColor: "#fff",
    gap: 8,
  },
  optionCardActive: {
    backgroundColor: "#111",
    borderColor: "#111",
  },
  optionTitle: {
    fontSize: 17,
    fontWeight: "900",
    color: "#111",
  },
  optionTitleActive: {
    color: "#fff",
  },
  valueOptionCard: {
    borderWidth: 1,
    borderColor: "#e3e3e3",
    borderRadius: 20,
    padding: 18,
    backgroundColor: "#fff",
  },
  valueOptionCardActive: {
    borderColor: BLUE,
    borderWidth: 2,
    backgroundColor: "#f8fbff",
  },
  valueOptionTitle: {
    fontSize: 19,
    fontWeight: "900",
    color: "#111",
  },
  valueOptionTitleActive: {
    color: BLUE,
  },
  valueOptionNote: {
    marginTop: 5,
    fontSize: 12,
    color: "#666",
    fontWeight: "800",
  },
  valueOptionNoteActive: {
    color: "#1f5fbf",
  },
  primaryButton: {
    marginTop: 20,
    backgroundColor: "#111",
    borderRadius: 17,
    paddingVertical: 15,
    alignItems: "center",
  },
  primaryButtonFlex: {
    flex: 1,
    backgroundColor: "#111",
    borderRadius: 17,
    paddingVertical: 15,
    alignItems: "center",
  },
  secondaryButton: {
    backgroundColor: "#efefef",
    borderRadius: 17,
    paddingVertical: 15,
    paddingHorizontal: 18,
    alignItems: "center",
  },
  primaryButtonText: {
    color: "#fff",
    fontWeight: "900",
    fontSize: 15,
  },
  secondaryButtonText: {
    color: "#111",
    fontWeight: "900",
    fontSize: 15,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 10,
    marginTop: 18,
    flexWrap: "wrap",
  },
  disabledButton: {
    opacity: 0.45,
  },
  label: {
    fontSize: 14,
    fontWeight: "900",
    color: "#111",
    marginBottom: 10,
    marginTop: 14,
  },
  wrapRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  smallPill: {
    paddingVertical: 10,
    paddingHorizontal: 13,
    borderRadius: 999,
    backgroundColor: "#f2f2f2",
    borderWidth: 1,
    borderColor: "#e3e3e3",
  },
  smallPillActive: {
    backgroundColor: "#111",
    borderColor: "#111",
  },
  smallPillText: {
    color: "#111",
    fontWeight: "800",
    fontSize: 13,
  },
  smallPillTextActive: {
    color: "#fff",
  },
  slotPreviewBox: {
    marginTop: 18,
    backgroundColor: "#f7f7f7",
    borderWidth: 1,
    borderColor: "#ececec",
    borderRadius: 16,
    padding: 13,
  },
  slotPreviewTitle: {
    fontWeight: "900",
    color: "#111",
    marginBottom: 4,
  },
  slotPreviewText: {
    color: "#666",
    fontSize: 13,
    lineHeight: 19,
  },
  warningBox: {
    marginTop: 18,
    backgroundColor: "#fff7df",
    borderWidth: 1,
    borderColor: "#f2d48a",
    borderRadius: 16,
    padding: 13,
    flexDirection: "row",
    gap: 9,
    alignItems: "flex-start",
  },
  warningText: {
    flex: 1,
    color: "#8a5a00",
    fontSize: 13,
    lineHeight: 19,
    fontWeight: "700",
  },
  loadingCard: {
    backgroundColor: "#111",
    borderRadius: 28,
    padding: 26,
    minHeight: 280,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 10 },
  },
  loadingSparkle: {
    width: 70,
    height: 70,
    borderRadius: 35,
    backgroundColor: "#fdf05d",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 18,
  },
  loadingSparkleText: {
    fontSize: 32,
  },
  loadingTitle: {
    color: "#fff",
    fontSize: 23,
    fontWeight: "800",
    marginBottom: 12,
  },
  loadingLine: {
    color: "#fdf05d",
    fontSize: 18,
    fontWeight: "900",
    textAlign: "center",
  },
  loadingDotsRow: {
    flexDirection: "row",
    gap: 7,
    marginTop: 20,
    marginBottom: 16,
  },
  loadingDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#fdf05d",
    opacity: 0.65,
  },
  loadingDotMid: {
    opacity: 1,
  },
  loadingSub: {
    color: "#d5d5d5",
    fontSize: 13,
    lineHeight: 19,
    textAlign: "center",
    maxWidth: 310,
    fontWeight: "600",
  },
  resultsHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  iconButton: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: "#f2f2f2",
    alignItems: "center",
    justifyContent: "center",
  },
  selectedSummaryBar: {
    backgroundColor: "#111",
    borderRadius: 20,
    padding: 15,
    marginBottom: 14,
  },
  selectedSummaryText: {
    color: "#fff",
    fontSize: 22,
    fontWeight: "900",
  },
  selectedSummarySub: {
    color: "#cfcfcf",
    fontSize: 13,
    marginTop: 4,
    fontWeight: "700",
  },
  recapShell: {
    marginTop: 6,
    backgroundColor: "#f7f7f7",
    borderWidth: 1,
    borderColor: "#ececec",
    borderRadius: 22,
    paddingVertical: 14,
    paddingLeft: 14,
    marginBottom: 14,
  },
  recapHeaderRow: {
    paddingRight: 14,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  recapTitle: {
    fontSize: 16,
    fontWeight: "900",
    color: "#111",
  },
  recapSub: {
    marginTop: 3,
    fontSize: 12,
    color: "#666",
    fontWeight: "700",
  },
  recapCountPill: {
    backgroundColor: "#111",
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: "center",
    justifyContent: "center",
  },
  recapCountText: {
    color: "#fff",
    fontWeight: "900",
  },
  recapScroll: {
    gap: 10,
    paddingRight: 14,
    paddingTop: 12,
  },
  recapCard: {
    width: 132,
    height: 164,
    borderRadius: 18,
    overflow: "hidden",
    backgroundColor: "#000",
    opacity: 0.55,
    borderWidth: 2,
    borderColor: "transparent",
  },
  recapCardSelected: {
    opacity: 1,
    borderColor: BLUE,
  },
  recapImage: {
    width: "100%",
    height: "100%",
    backgroundColor: "#eee",
  },
  recapOverlay: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 96,
    backgroundColor: "rgba(0,0,0,0.42)",
  },
  recapCardTop: {
    position: "absolute",
    top: 9,
    right: 9,
  },
  recapSelectBubble: {
    width: 27,
    height: 27,
    borderRadius: 14,
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
  },
  recapSelectBubbleActive: {
    backgroundColor: BLUE,
  },
  recapCardBottom: {
    position: "absolute",
    left: 10,
    right: 10,
    bottom: 10,
  },
  recapCardTitle: {
    color: "#fff",
    fontWeight: "900",
    fontSize: 13,
    lineHeight: 16,
  },
  recapArea: {
    marginTop: 4,
    color: "#fdf05d",
    fontSize: 11,
    fontWeight: "900",
  },
  batchHero: {
    backgroundColor: "#111",
    borderRadius: 22,
    padding: 18,
    marginTop: 14,
  },
  batchHeroTitle: {
    color: "#fff",
    fontSize: 18,
    fontWeight: "900",
    marginBottom: 6,
  },
  batchHeroText: {
    color: "#d6d6d6",
    fontSize: 13,
    lineHeight: 19,
    marginBottom: 14,
  },
  batchButton: {
    backgroundColor: BLUE,
    borderRadius: 16,
    paddingVertical: 14,
    paddingHorizontal: 15,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 8,
  },
  batchButtonText: {
    color: "#fff",
    fontWeight: "900",
    fontSize: 14,
  },
  batchFootnote: {
    color: "#bdbdbd",
    marginTop: 10,
    fontSize: 12,
    lineHeight: 17,
  },
  tapGuideText: {
    marginTop: 15,
    color: "#666",
    fontSize: 13,
    fontWeight: "800",
  },
  largeSelectButton: {
    backgroundColor: "#efefef",
    borderRadius: 18,
    paddingVertical: 15,
    paddingHorizontal: 18,
    alignItems: "center",
    flexDirection: "row",
    gap: 8,
    minWidth: 145,
    justifyContent: "center",
  },
  largeSelectButtonText: {
    color: "#111",
    fontWeight: "900",
    fontSize: 14,
  },
  emptyBox: {
    marginTop: 18,
    borderWidth: 1,
    borderColor: "#e3e3e3",
    borderRadius: 18,
    padding: 20,
    alignItems: "center",
    backgroundColor: "#fafafa",
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: "900",
    color: "#111",
  },
  emptyText: {
    color: "#666",
    marginTop: 6,
    textAlign: "center",
  },
  offerCard: {
    marginTop: 14,
    borderRadius: 22,
    overflow: "hidden",
    backgroundColor: "#fff",
    borderWidth: 2,
    borderColor: "#eeeeee",
  },
  offerCardSelected: {
    borderColor: BLUE,
    borderWidth: 3,
  },
  offerCardDeselected: {
    opacity: 0.55,
    borderColor: "#eeeeee",
  },
  offerImageWrap: {
    width: "100%",
    height: 145,
    backgroundColor: "#eee",
    position: "relative",
  },
  offerImage: {
    width: "100%",
    height: "100%",
    backgroundColor: "#eee",
  },
  statusRibbon: {
    position: "absolute",
    top: 10,
    left: 10,
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 13,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  statusRibbonSelected: {
    backgroundColor: BLUE,
  },
  statusRibbonDeselected: {
    backgroundColor: "#fff",
  },
  statusRibbonText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "900",
  },
  statusRibbonTextDeselected: {
    color: "#111",
  },
  offerBody: {
    padding: 14,
  },
  offerTop: {
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-start",
  },
  offerTitle: {
    fontSize: 19,
    fontWeight: "900",
    color: "#111",
  },
  neighbourhoodHero: {
    marginTop: 8,
    alignSelf: "flex-start",
    backgroundColor: "#fdf05d",
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 11,
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  neighbourhoodHeroText: {
    color: "#111",
    fontWeight: "900",
    fontSize: 15,
  },
  selectBadge: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: "#f2f2f2",
    alignItems: "center",
    justifyContent: "center",
  },
  selectBadgeActive: {
    backgroundColor: BLUE,
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 12,
    alignItems: "center",
  },
  valueText: {
    color: "#111",
    fontSize: 21,
    fontWeight: "900",
    paddingVertical: 3,
    paddingRight: 8,
  },
  websiteChip: {
    backgroundColor: "#f3f3f3",
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 13,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  websiteChipText: {
    color: "#111",
    fontSize: 14,
    fontWeight: "900",
  },
  offerProminentBox: {
    marginTop: 12,
    backgroundColor: "#fdf05d",
    borderWidth: 0,
    borderRadius: 16,
    padding: 13,
  },
  offerProminentLabel: {
    fontSize: 12,
    color: "#000",
    fontWeight: "900",
    marginBottom: 5,
  },
  offerProminentText: {
    fontSize: 15,
    color: "#000",
    lineHeight: 21,
    fontWeight: "900",
  },
  detailBoxBlack: {
    marginTop: 12,
    backgroundColor: "#111",
    borderRadius: 15,
    padding: 12,
  },
  detailLabelWhite: {
    fontSize: 12,
    color: "#bbb",
    fontWeight: "900",
    marginBottom: 5,
  },
  detailTextWhite: {
    fontSize: 14,
    color: "#fff",
    lineHeight: 20,
    fontWeight: "900",
  },
  errorBox: {
    backgroundColor: "#fff1f1",
    borderWidth: 1,
    borderColor: "#ffd1d1",
    borderRadius: 16,
    padding: 14,
    marginBottom: 16,
  },
  errorTitle: {
    color: "#9f0000",
    fontWeight: "900",
    marginBottom: 5,
  },
  errorText: {
    color: "#9f0000",
    fontSize: 13,
    lineHeight: 19,
  },
});